/**
 * Imperative widget renderers — the head-embed / vanilla-JS counterparts of
 * the React components in `./react/components`. They create STOQ's own
 * elements, insert them into a container you point at, wire the click →
 * modal → cart (or notify-me) flow, and return a {@link WidgetHandle} so you
 * can re-render on variant change or tear down.
 *
 * Why these exist: the React components can't be used in a head-embed (no JSX
 * in the page's HTML), and wiring `preorderButtonFor` + `openPreorderModal` +
 * `cartLineFor` by hand is fiddly. One `renderPreorderWidget({ variant,
 * container })` call does the lot, re-runnable.
 *
 * Self-contained, NOT theme-takeover: unlike the theme app embed's
 * `renderPreorderButton` (which mutates the theme's existing add-to-cart
 * button), these render their own element into a container you own — the
 * right model for headless storefronts that have no theme button to hijack.
 *
 * SSR-safe: nothing here touches `document` at module load; the render
 * functions require a browser and throw a clear error during SSR.
 */
import { injectComponentStyles } from './component-styles'
import { el } from './modal/dom'
import type { InlineFormOptions, ModalHandle, ModalOptions, ModalPrefill } from './modal'
import type {
  PreorderConfirmation,
  PreorderButtonContent,
  PreorderModalOptions,
} from './preorder'
import type { CartLine, VariantState } from './types'
import { warn } from './util'
import type { VariantInput } from './variant-input'

/**
 * The slice of {@link StoqClient} the widgets consume. The concrete client
 * satisfies this structurally; declaring it here keeps this module free of a
 * value import of the client (which imports this module).
 */
export interface WidgetClient {
  readonly signupsEnabled: boolean
  getVariantState(variant: VariantInput): Promise<VariantState>
  preorderButtonFor(variant: VariantInput): PreorderButtonContent | null
  openPreorderModal(options: PreorderModalOptions): Promise<PreorderConfirmation | null>
  openInlineForm(options: InlineFormOptions): ModalHandle | null
}

/** Where a widget renders. */
interface WidgetTarget {
  /** Target element or CSS selector. The widget is inserted relative to it. */
  container: Element | string
  /**
   * Insert position relative to `container` (same values as
   * `Element.insertAdjacentElement`). Defaults to `'beforeend'` (appended
   * inside the container).
   */
  insert?: InsertPosition
}

/** A mounted widget. Re-render in place with {@link update}; remove with {@link destroy}. */
export interface WidgetHandle {
  /** The currently rendered root element, or `null` when nothing is shown. */
  readonly element: HTMLElement | null
  /**
   * Re-resolves state and re-renders in place. Pass a new variant to switch
   * (e.g. the shopper picked a different variant). Returns once the new
   * element (or nothing) is in the DOM.
   */
  update(variant?: VariantInput): Promise<void>
  /** Removes the widget from the DOM and stops tracking its container. */
  destroy(): void
}

/** Options for {@link renderPreorderButton}. */
export interface PreorderButtonWidgetOptions extends WidgetTarget {
  /**
   * Called with the final Storefront Cart API line (and the full
   * confirmation) once the shopper confirms — pass the line to
   * `cartLinesAdd` / your cart form. The line carries the CHOSEN payment
   * option's selling plan id.
   */
  onAddToCart: (line: CartLine, confirmation: PreorderConfirmation) => void
  /** Quantity to add. Defaults to the plan's minimum (or 1). */
  quantity?: number
  /** Forwarded to the modal product header and event payloads. */
  productId?: number | string
  productTitle?: string
  variantTitle?: string
  imageUrl?: string
  /** Per-call modal overrides (texts/styles). */
  modal?: Pick<PreorderModalOptions, 'texts' | 'styles'>
  /** Button label. Defaults to the merchant-configured `preorder_button_text`. */
  label?: string
  /** Extra class(es) added alongside `stoq-preorder-button`. */
  className?: string
}

/** Options for {@link renderPreorderBadge}. */
export interface PreorderBadgeWidgetOptions extends WidgetTarget {
  /** Badge text. Defaults to `"Preorder"`. */
  text?: string
  /** Extra class(es) added alongside `stoq-preorder-badge`. */
  className?: string
}

/** Options for {@link renderNotifyMe}. */
export interface NotifyMeWidgetOptions extends WidgetTarget {
  /** Required — the back-in-stock signup is rejected without it. */
  productId: number | string
  productTitle?: string
  variantTitle?: string
  imageUrl?: string
  /** Pre-fill the email/phone fields. */
  prefill?: ModalPrefill
  /** Render the form immediately instead of a button that expands into it. */
  expanded?: boolean
  /** Trigger-button label (ignored when `expanded`). Defaults to `"Notify me when available"`. */
  label?: string
  /** Per-call form overrides (texts/styles). */
  form?: Pick<ModalOptions, 'texts' | 'styles'>
  /** Extra class(es) added alongside `stoq-notify-me`. */
  className?: string
}

/** Options for {@link renderPreorderWidget}. */
export interface PreorderWidgetOptions extends PreorderButtonWidgetOptions {
  /** Also render a `Preorder` badge before the button. */
  badge?: boolean | Pick<PreorderBadgeWidgetOptions, 'text' | 'className'>
  /**
   * When the variant is NOT preorder-eligible but back-in-stock signups are
   * enabled, render the notify-me widget instead of nothing. Requires
   * `productId`.
   */
  notifyMeFallback?: boolean
}

function cx(...parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

function requireDocument(method: string): Document {
  if (typeof document === 'undefined') {
    throw new Error(`[Stoq] ${method} requires a browser environment (no document found)`)
  }
  return document
}

function resolveContainer(doc: Document, container: Element | string): Element | null {
  return typeof container === 'string' ? doc.querySelector(container) : container
}

/** Mounted widget roots → their handle, so a re-render against the same container replaces it. */
const mountedWidgets = new Map<Element, WidgetHandle>()

const INERT_HANDLE: WidgetHandle = {
  element: null,
  update: () => Promise.resolve(),
  destroy: () => {},
}

/**
 * Shared mount machinery: resolves the container, replaces any widget already
 * mounted there, and renders via `render(variant)` (which returns the element
 * to show, or `null` to show nothing). Returns a handle wired for re-render
 * and teardown.
 */
async function createWidget(
  method: string,
  target: WidgetTarget,
  initialVariant: VariantInput,
  render: (variant: VariantInput) => Promise<HTMLElement | null>,
): Promise<WidgetHandle> {
  const doc = requireDocument(method)
  const container = resolveContainer(doc, target.container)
  if (!container) {
    warn(`${method}: container not found: ${String(target.container)}`)
    return INERT_HANDLE
  }

  mountedWidgets.get(container)?.destroy()
  injectComponentStyles(doc)

  const insert = target.insert ?? 'beforeend'
  let current: HTMLElement | null = null
  let variant = initialVariant

  const place = (next: HTMLElement | null): void => {
    if (current) current.remove()
    current = next
    if (next) container.insertAdjacentElement(insert, next)
  }

  const handle: WidgetHandle = {
    get element() {
      return current
    },
    async update(nextVariant?: VariantInput) {
      if (nextVariant !== undefined) variant = nextVariant
      place(await render(variant))
    },
    destroy() {
      if (current) current.remove()
      current = null
      if (mountedWidgets.get(container) === handle) mountedWidgets.delete(container)
    },
  }

  mountedWidgets.set(container, handle)
  place(await render(variant))
  return handle
}

/** Builds the preorder CTA `<button>` for a variant, or `null` when it isn't preorderable. */
async function buildPreorderButton(
  client: WidgetClient,
  doc: Document,
  variant: VariantInput,
  options: PreorderButtonWidgetOptions,
): Promise<HTMLElement | null> {
  const state = await client.getVariantState(variant)
  const content = client.preorderButtonFor(variant)
  if (!state.isPreorder || content === null) return null

  const button = el(doc, 'button', {
    className: cx('stoq-preorder-button', options.className),
    text: options.label ?? content.label,
    attrs: { type: 'button' },
  })

  let opening = false
  button.addEventListener('click', () => {
    if (opening) return
    opening = true
    button.disabled = true
    void client
      .openPreorderModal({
        variantId: variant,
        quantity: options.quantity,
        productId: options.productId,
        productTitle: options.productTitle,
        variantTitle: options.variantTitle,
        imageUrl: options.imageUrl,
        texts: options.modal?.texts,
        styles: options.modal?.styles,
      })
      .then((confirmation) => {
        if (confirmation !== null) options.onAddToCart(confirmation.cartLine, confirmation)
      })
      .catch((error: unknown) => warn('renderPreorderButton: openPreorderModal failed', error))
      .then(() => {
        opening = false
        button.disabled = false
      })
  })

  return button
}

/** Builds the preorder `<span>` badge for a variant, or `null` when it isn't preorderable. */
async function buildPreorderBadge(
  client: WidgetClient,
  doc: Document,
  variant: VariantInput,
  options: Pick<PreorderBadgeWidgetOptions, 'text' | 'className'>,
): Promise<HTMLElement | null> {
  const state = await client.getVariantState(variant)
  if (!state.isPreorder) return null
  return el(doc, 'span', {
    className: cx('stoq-preorder-badge', options.className),
    text: options.text ?? 'Preorder',
  })
}

/**
 * Builds the notify-me widget: a `<div class="stoq-notify-me">` wrapper
 * holding a trigger button that expands into the shop's inline signup form
 * (reusing {@link WidgetClient.openInlineForm}). With `expanded` the form is
 * shown straight away. `null` when signups are disabled for the shop.
 */
function buildNotifyMe(
  client: WidgetClient,
  doc: Document,
  variant: VariantInput,
  options: NotifyMeWidgetOptions,
): HTMLElement | null {
  if (!client.signupsEnabled) return null

  const wrapper = el(doc, 'div', { className: cx('stoq-notify-me', options.className) })

  const openForm = (): void => {
    client.openInlineForm({
      container: wrapper,
      variantId: variant,
      productId: options.productId,
      productTitle: options.productTitle,
      variantTitle: options.variantTitle,
      imageUrl: options.imageUrl,
      prefill: options.prefill,
      texts: options.form?.texts,
      styles: options.form?.styles,
    })
  }

  if (options.expanded) {
    openForm()
    return wrapper
  }

  const button = el(doc, 'button', {
    className: 'stoq-notify-me__button',
    text: options.label ?? 'Notify me when available',
    attrs: { type: 'button' },
  })
  button.addEventListener('click', () => {
    button.remove()
    openForm()
  })
  wrapper.appendChild(button)
  return wrapper
}

/**
 * Renders the preorder CTA button (imperative twin of `<StoqPreorderButton>`)
 * into `options.container`. Shows the button only when the variant is
 * currently purchasable as a preorder; otherwise nothing is rendered (and any
 * previously rendered button is removed). Clicking opens the payment-option
 * modal when a choice is required, otherwise confirms immediately; either way
 * `onAddToCart` receives the final cart line.
 *
 * Re-running against the same container replaces the previous widget. Use the
 * returned handle's `update(variant)` to re-render when the selected variant
 * changes.
 */
export function renderPreorderButton(
  client: WidgetClient,
  variant: VariantInput,
  options: PreorderButtonWidgetOptions,
): Promise<WidgetHandle> {
  return createWidget('renderPreorderButton', options, variant, (v) =>
    buildPreorderButton(client, requireDocument('renderPreorderButton'), v, options),
  )
}

/**
 * Renders a `Preorder` badge (imperative twin of `<StoqPreorderBadge>`) into
 * `options.container`, shown only while the variant is purchasable as a
 * preorder. Re-running against the same container replaces the previous badge.
 */
export function renderPreorderBadge(
  client: WidgetClient,
  variant: VariantInput,
  options: PreorderBadgeWidgetOptions,
): Promise<WidgetHandle> {
  return createWidget('renderPreorderBadge', options, variant, (v) =>
    buildPreorderBadge(client, requireDocument('renderPreorderBadge'), v, options),
  )
}

/**
 * Renders the notify-me widget (imperative twin of `<StoqNotifyMeButton>`)
 * into `options.container`: a button that expands into the shop's back-in-stock
 * signup form (or the form directly with `expanded`). Renders nothing when
 * signups are disabled for the shop. Visibility is intentionally NOT gated on
 * stock — gate the call yourself on your own availability data.
 */
export function renderNotifyMe(
  client: WidgetClient,
  variant: VariantInput,
  options: NotifyMeWidgetOptions,
): Promise<WidgetHandle> {
  return createWidget('renderNotifyMe', options, variant, (v) =>
    Promise.resolve(buildNotifyMe(client, requireDocument('renderNotifyMe'), v, options)),
  )
}

/**
 * The one-call "do everything" preorder widget. Renders the preorder badge
 * (when `badge` is set) and the preorder CTA button for the variant; with
 * `notifyMeFallback` it renders the notify-me widget instead when the variant
 * isn't preorder-eligible but signups are on. Everything goes into
 * `options.container`, re-runnable via the returned handle's `update(variant)`.
 */
export function renderPreorderWidget(
  client: WidgetClient,
  variant: VariantInput,
  options: PreorderWidgetOptions,
): Promise<WidgetHandle> {
  const badgeOptions = options.badge === true ? {} : (options.badge || null)

  return createWidget('renderPreorderWidget', options, variant, async (v) => {
    const doc = requireDocument('renderPreorderWidget')
    const button = await buildPreorderButton(client, doc, v, options)

    if (button === null) {
      if (options.notifyMeFallback) {
        return buildNotifyMe(client, doc, v, {
          container: options.container,
          productId: options.productId as number | string,
          productTitle: options.productTitle,
          variantTitle: options.variantTitle,
          imageUrl: options.imageUrl,
        })
      }
      return null
    }

    const root = el(doc, 'div', { className: 'stoq-preorder-widget' })
    if (badgeOptions !== null) {
      const badge = await buildPreorderBadge(client, doc, v, badgeOptions)
      if (badge) root.appendChild(badge)
    }
    root.appendChild(button)
    return root
  })
}
